/**
 * The application's nav table.
 *
 * Icons and paths live here; the LABELS do not, because a label is a translated
 * string and this module has no `t`. `App.tsx` joins the two, which is also what
 * keeps the nav in step with `i18n.changeLanguage` — a table of pre-translated
 * strings built once at module load would stay English forever.
 *
 * The first nine entries are the handoff's scope line: "Dashboard, Cases,
 * Dispensaries, Users, Requests, Knowledge Base, Justifications, Reports, Send
 * SMS". The icons for those nine are the handoff's own "Icons in use" list.
 *
 * `settings` is the tenth, added by M-01b-part-2b task 8 — the handoff
 * predates self-service and names no icon for it. `UserGearIcon` is this
 * repository's own choice (a person, plus the gear every other preference
 * control already uses — `Topbar.tsx`'s theme/density/locale switches read
 * `CircleHalfIcon`/`RowsIcon`/`TranslateIcon`; none of the three reads as
 * "this account", which a settings entry has to).
 *
 * `auditLog` is the eleventh, added by M-02b task 7. Unlike every entry
 * above it, it is NOT rendered unconditionally — `App.tsx` filters it out of
 * `navItems` for a session that does not hold `audit.view`, the same way
 * `activePath === '/users'` already gates what RENDERS (but not, until now,
 * what appears in the nav itself) for a caller without `user.view`.
 * `ClockCounterClockwiseIcon` is chosen over a shield or a scroll: the
 * handoff's own nine reserve the shield family for nothing in this table,
 * and a clock running backwards reads as "history", which is what the
 * screen behind this entry actually is.
 *
 * `devices` is the twelfth, added by M-04 task 10 — the device stock
 * registry (sensors, readers). Deliberately UNGATED here, the identical
 * shape `dispensaries` already has: every `GET /devices/*` route needs only
 * `auth:sanctum`/`active` (`devices.ts`'s own docblock), so there is no
 * permission this entry could 403 against — `DeviceScreen` itself is what
 * gates every create/edit/status control on `device.manage`. `CpuIcon`
 * (never `CircuitryIcon`, the package's other plausible pick for
 * "hardware"): a CPU reads as "a device", where circuitry reads as "wiring",
 * which is not what this screen manages.
 *
 * `cases` (the second of the original nine) BECAME GATED with M-06 task 5's
 * case register: `CasePolicy::view()` requires `case.view` for BOTH reads,
 * so `App.tsx` now filters this entry the identical `dispensaryCodes` shape
 * below, rather than leaving it ungated the way `dispensaries`/`devices`
 * still are.
 *
 * `replacements` is the FIFTEENTH, added by M-13 Task 5 — the device
 * replacement workflow, and the second entry (after `escalations`) that did
 * not exist here at all rather than one that merely pointed nowhere. The
 * legacy's own two links are COMMENTED OUT
 * (`includes/left-sidebar.php:161,166`) and no other sidebar mentions
 * replacements, so every page in that module was reachable only by typed
 * URL. Commenting out a link is not a decision to retire a feature; it is
 * the absence of one (M-13 spec §0). GATED, the `escalations` shape:
 * `ReplacementPolicy` (verified against the API repository) admits
 * `replacement.view` OR `replacement.fulfil`, via
 * `src/lib/permissions/replacements.ts`'s own `canReadReplacements` — TWO
 * permissions, because a fulfil-holder must never get 403 reading the row
 * it is about to act on.
 *
 * `escalations` is the fourteenth, added by M-10 Task 5 — the complaint
 * escalation chain. GATED, the `dispensaryCodes` shape: `EscalationPolicy`
 * (verified against the API repository) requires `escalation.view` for both
 * `GET /escalations` and `GET /escalations/{escalation}`. UNLIKE every
 * other entry in this table, this one did not point nowhere before this
 * milestone because it simply did not exist — spec §0 of M-10's own design
 * document records that the legacy hid this whole feature by commenting out
 * its own sidebar links (`includes/left-sidebar.php:174-197`), and "the nav
 * entry ships" is that same section's second consequence: hiding a feature
 * is not a decision, it is an absence of one. `WarningIcon`: a triangle
 * warning reads as "something needs attention", which is what a complaint
 * chain is, where `GavelIcon` (already the `justifications` entry's own
 * choice two rows up) reads as a ruling rather than a live workflow.
 *
 * `justifications` (the seventh of the original nine) BECAME GATED with
 * M-11 Task 4's staff conduct-complaint workflow: it pointed nowhere before
 * this task — no screen was ever wired to `/justifications` — and now that
 * one is, `JustificationPolicy` (verified against the API repository)
 * requires one of `justification.view`/`create`/`answer`/`decide` for both
 * `GET /justifications` and `GET /justifications/{justification}`.
 * `App.tsx` filters this entry the identical `dispensaryCodes`/
 * `escalations` shape below, via `src/lib/permissions/justifications.ts`'s
 * own `canReadJustifications`. Unlike `escalations`, this is not a case of
 * a permission no role grants: `RolePermissionSeeder::MATRIX` gives
 * `justification.view` to all five roles today, so this gate is written for
 * the role added later that might not.
 *
 * `dispensaryCodes` is the thirteenth, added by M-05 task 7 — the
 * dispensary code pool. UNLIKE `devices` immediately above, this entry is
 * GATED, the same shape `auditLog` already has: `codes.ts`'s own docblock is
 * explicit that all five `/dispensary-codes*` endpoints — including the two
 * reads — require `code.manage`, not merely an authenticated session (a
 * voucher string is worth money the moment it is claimed). `App.tsx` filters
 * this entry out of `navItems` for a session without it, the identical
 * `entry.id !== 'auditLog' || permissions.includes('audit.view')` shape
 * extended to a second id. `TicketIcon`: a voucher/code reads as a ticket to
 * redeem, not as the hardware `CpuIcon` already names.
 *
 * `knowledgeBase` lives in its OWN section, `knowledge`. It sat under
 * Messaging beside Send SMS and the templates, which made the call-centre's
 * reference library read as one more way of sending a text.
 *
 * `users` and `roles` moved from Directory to `administration`: managing
 * who can sign in and what they may do is administration, not part of the
 * dispensary hierarchy Directory holds.
 *
 * `adminSettings` is system configuration rather than a list of records —
 * today the escalation routing, which used to sit under the escalations
 * queue. GATED (on `escalation.manage`, the only setting it holds so far) and
 * in its own `administration` section.
 *
 * `support` is the sixteenth, added by M-15d Task 2 — a message box to the
 * support mailbox. Deliberately UNGATED, placed right after `settings`: every
 * signed-in staff member may reach it (spec D-3) because the endpoint behind
 * it (`POST /support-messages`) carries no permission either, the identical
 * reasoning `devices`/`dispensaries` above already give for staying out of
 * the `navItems` filter chain in `App.tsx`. `LifebuoyIcon`: a lifebuoy reads
 * as "get help", where `ChatCircleTextIcon` is already claimed twice above
 * for SMS/requests.
 *
 * ## Why every icon is a deep import
 *
 * `@phosphor-icons/react`'s package root re-exports every icon at every weight.
 * Importing one name from it pulls **4,606 modules** through the transform, and
 * task 24 measured what that produced the moment the shell became the first
 * thing the application actually mounted: 193.61 kB -> 252.35 kB. Rollup does
 * tree-shake the barrel, but only after transforming all of it, and the shaken
 * result still carried most of the cost.
 *
 * `@phosphor-icons/react/dist/csr/<Name>` is one file per icon and is a
 * documented entry point (`exports["./dist/csr/*"]` in the package manifest),
 * not a reach into private internals. `src/test/importDiscipline.test.ts` fails
 * the build if the barrel comes back — including from a test or a harness,
 * because `build:e2e` bundles those too.
 *
 * `import type { IconProps }` from the root is deliberately still allowed and is
 * what `shellState.ts` uses: a type-only import is erased before Rollup ever
 * sees it, so it costs nothing at runtime.
 */

import { BookOpenTextIcon } from '@phosphor-icons/react/dist/csr/BookOpenText';
import { ChartBarIcon } from '@phosphor-icons/react/dist/csr/ChartBar';
import { ChatCircleTextIcon } from '@phosphor-icons/react/dist/csr/ChatCircleText';
import { ClockCounterClockwiseIcon } from '@phosphor-icons/react/dist/csr/ClockCounterClockwise';
import { CpuIcon } from '@phosphor-icons/react/dist/csr/Cpu';
import { FoldersIcon } from '@phosphor-icons/react/dist/csr/Folders';
import { GearSixIcon } from '@phosphor-icons/react/dist/csr/GearSix';
import { GavelIcon } from '@phosphor-icons/react/dist/csr/Gavel';
import { LifebuoyIcon } from '@phosphor-icons/react/dist/csr/Lifebuoy';
import { NotePencilIcon } from '@phosphor-icons/react/dist/csr/NotePencil';
import { SquaresFourIcon } from '@phosphor-icons/react/dist/csr/SquaresFour';
import { StorefrontIcon } from '@phosphor-icons/react/dist/csr/Storefront';
import { SwapIcon } from '@phosphor-icons/react/dist/csr/Swap';
import { TicketIcon } from '@phosphor-icons/react/dist/csr/Ticket';
import { ShieldCheckIcon } from '@phosphor-icons/react/dist/csr/ShieldCheck';
import { UserGearIcon } from '@phosphor-icons/react/dist/csr/UserGear';
import { UsersThreeIcon } from '@phosphor-icons/react/dist/csr/UsersThree';
import { WarningIcon } from '@phosphor-icons/react/dist/csr/Warning';

/**
 * Deliberately NOT annotated `readonly NavEntry[]`: `as const` is what keeps
 * `labelKey` a literal union, and `i18next.d.ts` types `t()` against the real
 * bundle — so a key that does not exist is a compile error rather than a string
 * rendered raw on screen.
 */
export const NAV_ENTRIES = [
  {
    id: 'dashboard',
    labelKey: 'nav.dashboard',
    path: '/',
    icon: SquaresFourIcon,
    group: 'work',
  },
  {
    id: 'cases',
    labelKey: 'nav.cases',
    path: '/cases',
    icon: FoldersIcon,
    group: 'work',
  },
  {
    id: 'dispensaries',
    labelKey: 'nav.dispensaries',
    path: '/dispensaries',
    icon: StorefrontIcon,
    group: 'directory',
  },
  {
    id: 'dispensaryCodes',
    labelKey: 'nav.dispensaryCodes',
    path: '/dispensary-codes',
    icon: TicketIcon,
    group: 'directory',
  },
  {
    id: 'roles',
    labelKey: 'nav.roles',
    path: '/roles',
    icon: ShieldCheckIcon,
    group: 'administration',
  },
  {
    id: 'users',
    labelKey: 'nav.users',
    path: '/users',
    icon: UsersThreeIcon,
    group: 'administration',
  },
  {
    id: 'requests',
    labelKey: 'nav.requests',
    path: '/requests',
    icon: ChatCircleTextIcon,
    group: 'work',
  },
  {
    id: 'knowledgeBase',
    labelKey: 'nav.knowledgeBase',
    path: '/knowledge-base',
    icon: BookOpenTextIcon,
    group: 'knowledge',
  },
  {
    id: 'justifications',
    labelKey: 'nav.justifications',
    path: '/justifications',
    icon: GavelIcon,
    group: 'work',
  },
  {
    id: 'reports',
    labelKey: 'nav.reports',
    path: '/reports',
    icon: ChartBarIcon,
    group: 'insight',
  },
  {
    id: 'sendSms',
    labelKey: 'nav.sendSms',
    path: '/send-sms',
    icon: ChatCircleTextIcon,
    group: 'messaging',
  },
  {
    id: 'settings',
    labelKey: 'nav.settings',
    path: '/profile',
    icon: UserGearIcon,
    group: 'account',
  },
  {
    id: 'support',
    labelKey: 'nav.support',
    path: '/support',
    icon: LifebuoyIcon,
    group: 'account',
  },
  {
    id: 'auditLog',
    labelKey: 'nav.auditLog',
    path: '/audit-log',
    icon: ClockCounterClockwiseIcon,
    group: 'insight',
  },
  {
    id: 'devices',
    labelKey: 'nav.devices',
    path: '/devices',
    icon: CpuIcon,
    group: 'directory',
  },
  {
    id: 'escalations',
    labelKey: 'nav.escalations',
    path: '/escalations',
    icon: WarningIcon,
    group: 'work',
  },
  {
    id: 'replacements',
    labelKey: 'nav.replacements',
    path: '/replacements',
    icon: SwapIcon,
    group: 'work',
  },
  {
    id: 'adminSettings',
    labelKey: 'nav.adminSettings',
    path: '/admin-settings',
    icon: GearSixIcon,
    group: 'administration',
  },
  {
    id: 'smsTemplates',
    labelKey: 'nav.smsTemplates',
    path: '/sms-templates',
    icon: NotePencilIcon,
    group: 'messaging',
  },
] as const;

export type NavEntry = (typeof NAV_ENTRIES)[number];

/** The group ids `NAV_ENTRIES` above may name. */
export type NavGroupId = NavEntry['group'];

/**
 * The sidebar's sections, in the order they are rendered.
 *
 * Seventeen flat links was a list a reader scanned rather than navigated —
 * "Dispensary codes" sat eight rows below "Dispensaries" for no reason
 * except the order the milestones happened to land in. Grouping is what
 * makes "the code pool belongs to the dispensary hierarchy" visible without
 * anyone having to explain it.
 *
 * `account` is deliberately ABSENT from this table. Its two entries
 * (`settings`, `support`) are the shell's own chrome rather than a place
 * work happens: `settings` is reached from the topbar's account menu and
 * never renders in the sidebar at all, and `support` is pinned to the
 * sidebar's footer by `AppShell`. A group in this list is a section with a
 * heading; those two are neither.
 *
 * The labels are keys, not strings, for `navigation.ts`'s own reason: this
 * module has no `t`, and a table of pre-translated strings built once at
 * module load would stay English through every `changeLanguage`.
 */
export const NAV_GROUPS = [
  { id: 'work', labelKey: 'nav.groupWork' },
  { id: 'directory', labelKey: 'nav.groupDirectory' },
  { id: 'messaging', labelKey: 'nav.groupMessaging' },
  { id: 'knowledge', labelKey: 'nav.groupKnowledge' },
  { id: 'insight', labelKey: 'nav.groupInsight' },
  { id: 'administration', labelKey: 'nav.groupAdministration' },
] as const;

export type NavGroup = (typeof NAV_GROUPS)[number];
